import React, { useState } from 'react';
import { useQualityEvents } from '../../context/QualityEventsContext';
import { useFilters } from '../../context/FilterContext';
import { useApp } from '../../context/AppContext';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { toast } from 'sonner';
import TraceLabel from '../trace/TraceLabel';
import { useTrace } from '../../context/TraceContext';

const ChangeControlCreateDialog = ({ open, onOpenChange, onCreated }) => {
  const { createChangeControl } = useQualityEvents();
  const { plant, area } = useFilters();
  const { currentRole } = useApp();
  const { pidTraceMode } = useTrace();

  const [formData, setFormData] = useState({
    title: '',
    type: '',
    reason: '',
    riskLevel: 'Medium',
    impactAssessment: {
      quality: 'Low',
      safety: 'Low',
      compliance: 'Low',
      delivery: 'Low'
    },
    approvers: [],
    targetDate: new Date(Date.now() + 60 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const newCC = createChangeControl({
        title: formData.title,
        type: formData.type,
        reason: formData.reason,
        riskLevel: formData.riskLevel,
        impactAssessment: formData.impactAssessment,
        approvers: formData.approvers,
        targetDate: formData.targetDate,
        area: area !== 'all' ? area : null,
        plant
      });

      toast.success('Change Control created', {
        description: `Created ${newCC.id}`
      });

      onCreated?.(newCC);
      onOpenChange(false);
      
      // Reset form
      setFormData({
        title: '',
        type: '',
        reason: '',
        riskLevel: 'Medium',
        impactAssessment: {
          quality: 'Low',
          safety: 'Low',
          compliance: 'Low',
          delivery: 'Low'
        },
        approvers: [],
        targetDate: new Date(Date.now() + 60 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
      });
    } catch (error) {
      toast.error('Failed to create change control', {
        description: error.message
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={`sm:max-w-[600px] max-h-[90vh] overflow-y-auto ${pidTraceMode ? 'border-cyan-300' : ''}`}>
        <TraceLabel prd="PRD §6.5" req="SM-CC-001" />
        <DialogHeader>
          <DialogTitle>New Change Control</DialogTitle>
          <DialogDescription>
            Create a new change control request. All fields marked with * are required.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="title">Change Title *</Label>
            <Input
              id="title"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="Brief description of the change"
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="type">Change Type *</Label>
              <Select
                value={formData.type}
                onValueChange={(value) => setFormData({ ...formData, type: value })}
                required
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Process">Process</SelectItem>
                  <SelectItem value="Equipment">Equipment</SelectItem>
                  <SelectItem value="Document">Document</SelectItem>
                  <SelectItem value="Facility">Facility</SelectItem>
                  <SelectItem value="Utility">Utility</SelectItem>
                  <SelectItem value="Software">Software</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="riskLevel">Risk Level *</Label>
              <Select
                value={formData.riskLevel}
                onValueChange={(value) => setFormData({ ...formData, riskLevel: value })}
                required
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Low">Low</SelectItem>
                  <SelectItem value="Medium">Medium</SelectItem>
                  <SelectItem value="High">High</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="reason">Reason for Change *</Label>
            <Textarea
              id="reason"
              value={formData.reason}
              onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
              placeholder="Explain why this change is needed"
              rows={4}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="targetDate">Target Date *</Label>
            <Input
              id="targetDate"
              type="date"
              value={formData.targetDate}
              onChange={(e) => setFormData({ ...formData, targetDate: e.target.value })}
              required
            />
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="bg-[#fd4816] hover:bg-[#ea380c] text-white"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Creating...' : 'Create Change Control'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ChangeControlCreateDialog;

